import React, { useEffect, useState } from 'react';
import { Button } from '@fiverr-private/button';
import { raiseToast } from '@fiverr-private/toast';
import { Gql } from '@fiverr-private/dynamic_matching';
import { SpinnerIcon } from '@fiverr-private/visuals';
import pathfinder from '@fiverr-private/pathfinder';
import { Stack } from '@fiverr-private/layout_components';
import { trackEvent } from '@fiverr-private/gtm';
import { useBriefContext } from '@fiverr-private/ai_brief/dist/esm/src/common/BriefContext';
import { guardWithActivation, PhoneVerificationVariant } from '@fiverr-private/code_activation_triggers';
import { useBriefCreationContext } from '../../context/BriefCreationContext';
import { AppI18n, appTranslate, type FormattedTranslationKey } from '../../i18n';
import { useSubmitBrief, useGetCurrentFlow } from '../../hooks';
import { GTM_EVENTS, Step } from '../../constants';
import { mixpanelEvents } from '../../monitoring';
import { buildUrlWithFromApp } from '../../utils';

interface SubmitBriefButtonProps {
  compactOnMobile?: boolean;
}

export const SubmitBriefButton = ({ compactOnMobile = false }: SubmitBriefButtonProps) => {
  const { setStep, setBrief, missingFieldsCount, buyerType, variant, inJpHighSpendExperience, source } =
    useBriefCreationContext();
  const { editedSectionId } = useBriefContext();
  const flow = useGetCurrentFlow();
  const { handleSubmit, isBriefLoading } = useSubmitBrief();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [rncAuctionFlow, setRncAuctionFlow] = useState(false);

  const isRncBuyer = buyerType === Gql.BuyerType.RegisteredNotConverted;

  useEffect(() => {
    setRncAuctionFlow(flow === Gql.DmMatchCandidateCreationFlow.Auction && isRncBuyer);
  }, [flow, isRncBuyer]);

  const doSubmit = async (preferredRncFlow?: Gql.DmMatchCandidateCreationFlow) => {
    if (
      (flow === Gql.DmMatchCandidateCreationFlow.Auction && !rncAuctionFlow) ||
      preferredRncFlow === Gql.DmMatchCandidateCreationFlow.Auction
    ) {
      setStep(Step.Finish);
    }

    let dmFlowAsBriefTier: Gql.DmBriefTier | undefined;

    const isInviteFreelancersFlow = preferredRncFlow === Gql.DmMatchCandidateCreationFlow.BuyerInvite;
    const isSourceForMeFlow = preferredRncFlow === Gql.DmMatchCandidateCreationFlow.Auction;

    if (isInviteFreelancersFlow) {
      dmFlowAsBriefTier = Gql.DmBriefTier.ManualInvites;
      mixpanelEvents.seamless(variant).editBrief.inviteFreelancers('Click');
    } else if (isSourceForMeFlow) {
      dmFlowAsBriefTier = Gql.DmBriefTier.Auction;
      mixpanelEvents.seamless(variant).editBrief.sourceForMe('Click');
    }

    const briefId = await handleSubmit(dmFlowAsBriefTier);

    if (!briefId) {
      raiseToast({
        type: 'error',
        location: 'top',
        message: appTranslate('something_went_wrong'),
      });

      if (isInviteFreelancersFlow) mixpanelEvents.seamless(variant).editBrief.inviteFreelancers('Failed');
      if (isSourceForMeFlow) mixpanelEvents.seamless(variant).editBrief.sourceForMe('Failed');

      setStep(Step.Edit);
      return;
    }

    setBrief((brief: any) => ({ ...brief, id: briefId }));

    trackEvent(GTM_EVENTS.DM_POST);
    mixpanelEvents.dmBriefCreationDrawerCreateClick();

    const shouldRedirectToInvite =
      !inJpHighSpendExperience &&
      ((flow === Gql.DmMatchCandidateCreationFlow.BuyerInvite && !rncAuctionFlow) || isInviteFreelancersFlow);

    if (shouldRedirectToInvite) {
      mixpanelEvents.seamless(variant).editBrief.inviteFreelancers('Success');
      setIsRedirecting(true);
      const basePath = pathfinder('dynamic_matching_management_page', {
        briefId,
        tab: 'invite',
      });
      window.location.href = buildUrlWithFromApp(basePath);
    } else {
      mixpanelEvents.seamless(variant).editBrief.sourceForMe('Success');
      setStep(Step.Finish);
    }
  };

  const submitAction = async (preferredRncFlow?: Gql.DmMatchCandidateCreationFlow) => {
    await guardWithActivation({
      sourceComponent: `${source ?? 'unknown'}_post_brief`,
      onAlreadyActivated: () => doSubmit(preferredRncFlow),
      phoneVerificationConfig: { variant: PhoneVerificationVariant.POST_BRIEF },
    });
  };

  const isDisabled = !!missingFieldsCount || !!editedSectionId;
  const shouldFullWidth = !compactOnMobile;

  const getButtonTextKey = (): FormattedTranslationKey => {
    if (inJpHighSpendExperience) {
      return 'post_your_brief';
    }

    return flow === Gql.DmMatchCandidateCreationFlow.BuyerInvite ? 'invite_freelancers' : 'post_your_brief';
  };

  const defaultFlowAction = (
    <Button
      fullWidth={{ default: shouldFullWidth, sm: false }}
      disabled={isDisabled}
      onClick={() => submitAction()}
      colorScheme={isBriefLoading || isRedirecting ? 'grey' : undefined}
    >
      {(isBriefLoading || isRedirecting) && <SpinnerIcon />}
      <AppI18n k={getButtonTextKey()} />
    </Button>
  );

  const rncAuctionFlowAction = (
    <Stack
      width={{
        default: shouldFullWidth ? '100%' : 'auto',
        sm: 'auto',
      }}
      gap="2"
      direction={{
        default: 'columnReverse',
        sm: 'row',
      }}
    >
      {!missingFieldsCount && (
        <Button
          variant="outline"
          fullWidth={{ default: shouldFullWidth, sm: false }}
          disabled={isDisabled}
          onClick={() => submitAction(Gql.DmMatchCandidateCreationFlow.Auction)}
          colorScheme={isBriefLoading || isRedirecting ? 'grey' : undefined}
        >
          <AppI18n k="source_for_me" />
        </Button>
      )}
      <Button
        fullWidth={{ default: shouldFullWidth, sm: false }}
        disabled={isDisabled}
        onClick={() => submitAction(Gql.DmMatchCandidateCreationFlow.BuyerInvite)}
        colorScheme={isBriefLoading || isRedirecting ? 'grey' : undefined}
      >
        {(isBriefLoading || isRedirecting) && <SpinnerIcon />}
        <AppI18n k="invite_freelancers" />
      </Button>
    </Stack>
  );

  if (inJpHighSpendExperience || !rncAuctionFlow) {
    return defaultFlowAction;
  }

  return rncAuctionFlowAction;
};
